import React, { useState, useCallback } from 'react';
import { List } from '@refinedev/antd';
import { useInvalidate } from '@refinedev/core';
import {
  Table,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Button,
  Radio,
  Spin,
  Empty,
  message,
  Drawer,
  Card,
  Image as AntdImage,
  Typography,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  SearchOutlined,
  MergeCellsOutlined,
  SwapOutlined,
  CheckOutlined,
  RobotOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { axiosInstance, API_BASE_URL } from '../../providers/dataProvider';
import { ALL_DISH_CATEGORIES } from '@wellverse/shared-ts';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface AiDishRow {
  id: string;
  name: string;
  category: string;
  type: string;
  standardPortionGrams: number;
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  fiber: number;
  sodium: number;
  aliases: string[];
  translations: Record<string, { name?: string }> | null;
  verifiedBy: string | null;
  createdAt: string;
}

interface SimilarDish {
  id: string;
  name: string;
  category: string;
  confidence: number;
  imageUrl: string | null;
}

interface AiListResponse {
  items: AiDishRow[];
  total: number;
  page: number;
  limit: number;
}

interface ExpandedRowState {
  similar: SimilarDish[];
  loading: boolean;
  error: string | null;
}

interface DuplicateMember {
  id: string;
  name: string;
  category: string;
  calories: number;
  protein: number;
  fat: number;
  carbs: number;
  standardPortionGrams: number;
  createdAt: string;
  imageUrl: string | null;
}

interface DuplicateGroup {
  id: string;
  memberCount: number;
  members: DuplicateMember[];
}

export const AiReviewList: React.FC = () => {
  const invalidate = useInvalidate();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const [data, setData] = useState<AiListResponse>({ items: [], total: 0, page: 1, limit });
  const [loading, setLoading] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [expandedState, setExpandedState] = useState<Record<string, ExpandedRowState>>({});
  const [pickedSimilar, setPickedSimilar] = useState<Record<string, string | null>>({});
  const [actionLoading, setActionLoading] = useState(false);

  // Smart Scan drawer state
  const [smartScanOpen, setSmartScanOpen] = useState(false);
  const [smartScanLoading, setSmartScanLoading] = useState(false);
  const [smartScanError, setSmartScanError] = useState<string | null>(null);
  const [groups, setGroups] = useState<DuplicateGroup[]>([]);
  const [groupTargets, setGroupTargets] = useState<Record<string, string>>({}); // groupId → picked member id
  const [mergingGroupId, setMergingGroupId] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (search) params.set('search', search);
      if (category) params.set('category', category);
      if (dateRange && dateRange[0]) params.set('fromDate', dateRange[0].toISOString());
      if (dateRange && dateRange[1]) params.set('toDate', dateRange[1].toISOString());

      const res = await axiosInstance.get(`${API_BASE_URL}/dish-catalog/admin/ai-generated?${params}`);
      const payload = res.data?.data ?? res.data;
      setData(payload ?? { items: [], total: 0, page, limit });
    } catch (err) {
      message.error('Failed to load AI review queue');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, category, dateRange]);

  React.useEffect(() => { fetchList(); }, [fetchList]);

  const fetchSimilar = useCallback(async (dishId: string) => {
    setExpandedState((s) => ({ ...s, [dishId]: { similar: [], loading: true, error: null } }));
    try {
      const res = await axiosInstance.get(`${API_BASE_URL}/dish-catalog/${dishId}/similar?limit=10`);
      const payload: SimilarDish[] = Array.isArray(res.data) ? res.data : (res.data?.items ?? []);
      setExpandedState((s) => ({ ...s, [dishId]: { similar: payload, loading: false, error: null } }));
    } catch (err) {
      setExpandedState((s) => ({ ...s, [dishId]: { similar: [], loading: false, error: 'Failed to load similar dishes' } }));
      console.error(err);
    }
  }, []);

  const onExpand = (expanded: boolean, record: AiDishRow) => {
    if (expanded) {
      setExpandedRowId(record.id);
      if (!expandedState[record.id]) fetchSimilar(record.id);
    } else {
      setExpandedRowId(null);
    }
  };

  const refreshAfterAction = () => {
    setSelectedRowKeys([]);
    setPickedSimilar({});
    setExpandedRowId(null);
    fetchList();
    invalidate({ resource: 'dish-catalog', invalidates: ['list'] });
  };

  const doMerge = async (aiId: string, targetId: string) => {
    setActionLoading(true);
    try {
      await axiosInstance.post(`${API_BASE_URL}/dish-catalog/admin/${aiId}/merge`, { targetId });
      message.success('Merged AI row into target dish');
      refreshAfterAction();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Merge failed');
    } finally {
      setActionLoading(false);
    }
  };

  const doReplace = async (aiId: string, targetId: string) => {
    setActionLoading(true);
    try {
      await axiosInstance.post(`${API_BASE_URL}/dish-catalog/admin/${aiId}/replace`, { targetId });
      message.success('Replaced target dish with AI row');
      refreshAfterAction();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Replace failed');
    } finally {
      setActionLoading(false);
    }
  };

  const doMarkReviewed = async (aiId: string) => {
    setActionLoading(true);
    try {
      await axiosInstance.post(`${API_BASE_URL}/dish-catalog/admin/${aiId}/review`);
      message.success('Marked as reviewed');
      refreshAfterAction();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Mark reviewed failed');
    } finally {
      setActionLoading(false);
    }
  };

  const doBulkAutoMerge = async () => {
    if (selectedRowKeys.length === 0) return;
    setActionLoading(true);
    try {
      const res = await axiosInstance.post(`${API_BASE_URL}/dish-catalog/admin/bulk-auto-merge`, {
        aiIds: selectedRowKeys,
        minConfidence: 0.85,
      });
      const result = res.data?.data ?? res.data;
      message.success(`Auto-merged ${result.merged} row(s); skipped ${result.skipped}`);
      refreshAfterAction();
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Bulk auto-merge failed');
    } finally {
      setActionLoading(false);
    }
  };

  const openSmartScan = async () => {
    setSmartScanOpen(true);
    setSmartScanLoading(true);
    setSmartScanError(null);
    try {
      const res = await axiosInstance.get(`${API_BASE_URL}/dish-catalog/admin/ai-generated/duplicate-groups?minConfidence=0.6`);
      const payload: DuplicateGroup[] = res.data?.data ?? res.data ?? [];
      setGroups(payload);
      // Default canonical = oldest (members already sorted asc by createdAt from API).
      const defaults: Record<string, string> = {};
      for (const g of payload) {
        if (g.members.length > 0) defaults[g.id] = g.members[0].id;
      }
      setGroupTargets(defaults);
    } catch (err: any) {
      setSmartScanError(err?.response?.data?.message ?? 'Failed to scan for duplicates');
    } finally {
      setSmartScanLoading(false);
    }
  };

  const doMergeGroup = async (group: DuplicateGroup) => {
    const targetId = groupTargets[group.id];
    if (!targetId) {
      message.warning('Pick a canonical row first');
      return;
    }
    setMergingGroupId(group.id);
    try {
      await axiosInstance.post(`${API_BASE_URL}/dish-catalog/admin/merge-group`, {
        aiIds: group.members.map((m) => m.id),
        targetId,
      });
      message.success(`Merged ${group.memberCount} duplicates into 1 row`);
      // Remove this group from the drawer; refresh main list.
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
      setGroupTargets((prev) => {
        const next = { ...prev };
        delete next[group.id];
        return next;
      });
      fetchList();
      invalidate({ resource: 'dish-catalog', invalidates: ['list'] });
    } catch (err: any) {
      message.error(err?.response?.data?.message ?? 'Group merge failed');
    } finally {
      setMergingGroupId(null);
    }
  };

  return (
    <List resource="ai-review" title="AI Scan Logs">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search by name or alias..."
          allowClear
          style={{ width: 280 }}
          onSearch={(v) => { setPage(1); setSearch(v); }}
        />
        <Select
          placeholder="Filter by category"
          allowClear
          showSearch
          style={{ width: 220 }}
          value={category}
          onChange={(v) => { setPage(1); setCategory(v); }}
          options={ALL_DISH_CATEGORIES.map((c) => ({ label: c, value: c }))}
        />
        <RangePicker
          value={dateRange as any}
          onChange={(range) => { setPage(1); setDateRange(range as any); }}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchList}>Refresh</Button>
        <Tooltip title="Clusters all unreviewed AI rows into duplicate groups via fuzzy name matching. Pick the canonical row per group, then merge.">
          <Button
            icon={<ThunderboltOutlined />}
            onClick={openSmartScan}
          >
            Smart Scan
          </Button>
        </Tooltip>
        <Tooltip title="Merges each selected row into its top-1 match when confidence ≥ 85%. Below that, row is left in queue.">
          <Button
            type="primary"
            icon={<RobotOutlined />}
            disabled={selectedRowKeys.length === 0}
            loading={actionLoading}
            onClick={doBulkAutoMerge}
          >
            Bulk auto-merge ({selectedRowKeys.length})
          </Button>
        </Tooltip>
      </div>

      <Table
        rowKey="id"
        dataSource={data.items}
        loading={loading}
        scroll={{ x: 1400 }}
        size="middle"
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        pagination={{
          current: data.page,
          pageSize: data.limit,
          total: data.total,
          onChange: (p, ps) => { setPage(p); setLimit(ps); },
          showSizeChanger: true,
          pageSizeOptions: [20, 50, 100, 500, 1000],
        }}
        expandable={{
          expandedRowKeys: expandedRowId ? [expandedRowId] : [],
          onExpand,
          expandedRowRender: (row) => (
            <ExpandedDetail
              state={expandedState[row.id] ?? { similar: [], loading: false, error: null }}
              pickedId={pickedSimilar[row.id] ?? null}
              onPick={(id) => setPickedSimilar((s) => ({ ...s, [row.id]: id }))}
              onMerge={() => {
                const tid = pickedSimilar[row.id];
                if (!tid) { message.warning('Pick a similar dish first'); return; }
                doMerge(row.id, tid);
              }}
              onReplace={() => {
                const tid = pickedSimilar[row.id];
                if (!tid) { message.warning('Pick a similar dish first'); return; }
                doReplace(row.id, tid);
              }}
              onMarkReviewed={() => doMarkReviewed(row.id)}
              actionLoading={actionLoading}
            />
          ),
        }}
      >
        <Table.Column dataIndex="name" title="Name" fixed="left" width={220} />
        <Table.Column
          title="Translations"
          width={140}
          render={(_: any, record: AiDishRow) => {
            const t = record.translations;
            if (!t || Object.keys(t).length === 0) return <Text type="secondary">—</Text>;
            return (
              <Space wrap size={4}>
                {Object.entries(t).slice(0, 2).map(([locale, data]) => (
                  <Tag key={locale} style={{ borderRadius: 9999, padding: '2px 8px', fontSize: 12 }}>
                    {locale}: {data.name}
                  </Tag>
                ))}
                {Object.keys(t).length > 2 && (
                  <Tag style={{ borderRadius: 9999, padding: '2px 8px', fontSize: 12 }}>+{Object.keys(t).length - 2}</Tag>
                )}
              </Space>
            );
          }}
        />
        <Table.Column dataIndex="category" title="Category" width={120} />
        <Table.Column dataIndex="standardPortionGrams" title="Portion (g)" width={100} />
        <Table.Column dataIndex="calories" title="Calories" width={100} />
        <Table.Column dataIndex="protein" title="Protein (g)" width={100} />
        <Table.Column dataIndex="fat" title="Fat (g)" width={90} />
        <Table.Column dataIndex="carbs" title="Carbs (g)" width={100} />
        <Table.Column
          dataIndex="createdAt"
          title="Created"
          width={140}
          render={(ts: string) => dayjs(ts).format('YYYY-MM-DD HH:mm')}
        />
      </Table>

      <Drawer
        title="Smart Scan — Duplicate Groups"
        placement="right"
        width={620}
        open={smartScanOpen}
        onClose={() => setSmartScanOpen(false)}
        destroyOnClose
      >
        {smartScanLoading && (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Spin tip="Clustering duplicates…" />
          </div>
        )}
        {smartScanError && (
          <Text type="danger">{smartScanError}</Text>
        )}
        {!smartScanLoading && !smartScanError && groups.length === 0 && (
          <Empty description="No duplicate groups found at threshold 0.6" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
        {!smartScanLoading && !smartScanError && groups.length > 0 && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Text type="secondary">
              {groups.length} group{groups.length === 1 ? '' : 's'} found · pick the canonical row per group, then merge.
              Members are sorted oldest-first (oldest = likely most data).
            </Text>
            {groups.map((group) => (
              <Card
                key={group.id}
                size="small"
                title={
                  <Space>
                    <Tag color="purple" style={{ borderRadius: 9999 }}>{group.memberCount} duplicates</Tag>
                  </Space>
                }
              >
                <Radio.Group
                  value={groupTargets[group.id]}
                  onChange={(e) => setGroupTargets((prev) => ({ ...prev, [group.id]: e.target.value }))}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {group.members.map((m, idx) => (
                      <Radio key={m.id} value={m.id} style={{ alignItems: 'flex-start', padding: '4px 0' }}>
                        <Space size={10} align="center">
                          {m.imageUrl && (
                            <AntdImage
                              src={m.imageUrl}
                              width={40}
                              height={40}
                              style={{ objectFit: 'cover', borderRadius: 4 }}
                              preview={false}
                            />
                          )}
                          <div>
                            <Space size={6}>
                              <Text strong>{m.name}</Text>
                              {idx === 0 && <Tag style={{ fontSize: 11, borderRadius: 9999 }}>oldest</Tag>}
                            </Space>
                            <div style={{ fontSize: 12, color: '#888' }}>
                              {m.category} · {m.calories} kcal · {m.protein}p/{m.fat}f/{m.carbs}c · {dayjs(m.createdAt).format('YYYY-MM-DD')}
                            </div>
                          </div>
                        </Space>
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
                <div style={{ marginTop: 12, textAlign: 'right' }}>
                  <Popconfirm
                    title="Merge this group?"
                    description={`All non-canonical rows will be folded into the picked one. This cannot be undone.`}
                    okText="Merge"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => doMergeGroup(group)}
                  >
                    <Button
                      type="primary"
                      icon={<MergeCellsOutlined />}
                      loading={mergingGroupId === group.id}
                    >
                      Merge into picked
                    </Button>
                  </Popconfirm>
                </div>
              </Card>
            ))}
          </Space>
        )}
      </Drawer>
    </List>
  );
};

const ExpandedDetail: React.FC<{
  state: ExpandedRowState;
  pickedId: string | null;
  onPick: (id: string) => void;
  onMerge: () => void;
  onReplace: () => void;
  onMarkReviewed: () => void;
  actionLoading: boolean;
}> = ({ state, pickedId, onPick, onMerge, onReplace, onMarkReviewed, actionLoading }) => {
  if (state.loading) {
    return <div style={{ padding: 12 }}><Spin size="small" /> Loading similar dishes…</div>;
  }
  if (state.error) {
    return <div style={{ padding: 12 }}><Text type="danger">{state.error}</Text></div>;
  }
  if (state.similar.length === 0) {
    return (
      <div style={{ padding: 12 }}>
        <Empty description="No similar dishes found — mark as reviewed if this is a valid new entry" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        <Button type="primary" icon={<CheckOutlined />} onClick={onMarkReviewed} loading={actionLoading} style={{ marginTop: 12 }}>
          Mark reviewed
        </Button>
      </div>
    );
  }
  return (
    <div style={{ padding: 12 }}>
      <Space align="start" style={{ width: '100%' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong>Pick a similar dish to merge or replace:</Text>
          <Radio.Group
            value={pickedId}
            onChange={(e) => onPick(e.target.value)}
            style={{ width: '100%', marginTop: 8 }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {state.similar.map((d) => (
                <Radio key={d.id} value={d.id} style={{ alignItems: 'flex-start', padding: '4px 0' }}>
                  <Space size={12} align="center">
                    {d.imageUrl && (
                      <AntdImage
                        src={d.imageUrl}
                        width={48}
                        height={48}
                        style={{ objectFit: 'cover', borderRadius: 6 }}
                        preview={false}
                      />
                    )}
                    <div>
                      <div style={{ fontWeight: 500 }}>{d.name}</div>
                      <Space size={6}>
                        <Tag style={{ borderRadius: 9999, padding: '0 8px', fontSize: 11 }}>{d.category}</Tag>
                        <Text type={d.confidence >= 0.85 ? 'success' : d.confidence >= 0.7 ? 'warning' : 'secondary'}>
                          {Math.round(d.confidence * 100)}% match
                        </Text>
                      </Space>
                    </div>
                  </Space>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>
        <div style={{ minWidth: 180 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Tooltip title={pickedId ? '' : 'Pick a similar dish above first'}>
              <Button
                block
                icon={<MergeCellsOutlined />}
                disabled={!pickedId || actionLoading}
                loading={actionLoading}
                onClick={onMerge}
              >
                Merge into picked
              </Button>
            </Tooltip>
            <Popconfirm
              title="Replace the picked dish?"
              description="Overwrites the picked dish's name, nutrition, and ingredients with this AI row's data. The picked row's existing data is lost."
              okText="Replace"
              okButtonProps={{ danger: true }}
              disabled={!pickedId || actionLoading}
              onConfirm={onReplace}
            >
              <Button block icon={<SwapOutlined />} disabled={!pickedId || actionLoading} loading={actionLoading}>
                Replace picked
              </Button>
            </Popconfirm>
            <Button block icon={<CheckOutlined />} disabled={actionLoading} loading={actionLoading} onClick={onMarkReviewed}>
              Mark reviewed
            </Button>
          </Space>
        </div>
      </Space>
    </div>
  );
};
